import {
    assertNotNullable,
    asyncCachedValueCreate,
    asyncWithFallback,
    Evaluable,
    evaluateWhenFunction,
    InitializableValue,
    RestfulApiHttpClient,
    restfulApiHttpClientCreate,
    urlModifyQueryParams,
    valueIsNotNullable
} from '@wix/devzai-utils-common';
import React, {useContext} from 'react';
import {BymoTemplatesStore} from "../../client-server-common/bymo-templates-store/bymo-templates-store";
import {
    DoppeActionTypesStore,
    doppeActionTypesStoreCreate
} from "../common-client-services/doppe-action-types-store/doppe-action-types-store";
import {DoppeViewerAppAnalyticsClient} from "../doppe-viewer-app-client-services/doppe-viewer-app-analytics-client";
import {IDoppeViewerApi} from "../../client-server-common/api/doppe-viewer-api";
import {DoppeUserAppData} from "../app-data/doppe-user-app-data";
import {DoppeViewerAppUiService} from "../doppe-viewer-app-client-services/doppe-viewer-app-ui-service";
import {DoppeViewerAppBymoService} from "../doppe-viewer-app-client-services/doppe-viewer-app-bymo-service";
import {DoppeViewerAppStripeService} from "../doppe-viewer-app-client-services/doppe-viewer-app-stripe-service";
import {DoppeViewerClientDataService} from "../doppe-viewer-app-client-services/doppe-viewer-client-data-service";
import {
    DoppeClientErrorsReportingService
} from "../common-client-services/doppe-client-errors-reporting-service/doppe-client-errors-reporting-service";
import {doppeClientHttpClientCreate} from "../../doppe-client-http-client/doppe-client-http-client";
import {
    WixAppsIds,
    wixDeployPreviewGetHeaders,
    WixDynamicModelV2,
    wixDynamicModelV2ResolveSignedInstance
} from "@wix/devzai-common-wix";
import {DoppeViewerAppCookieService} from "../doppe-viewer-app-client-services/doppe-viewer-app-cookie-service";
import {DoppeViewerAppLockService} from '../doppe-viewer-app-client-services/doppe-viewer-app-lock-service';
import {DoppeViewerBmAnalyticsDashboardBiClient} from '../doppe-viewer-app-client-services/doppe-viewer-bm-analytics-dashboard-bi-client';
import {DoppeViewerRouterClientService} from '../doppe-viewer-app-client-services/doppe-viewer-router-client-service';
import {DoppeViewerPagesRoutesManager} from '../../client-server-common';

export class DoppeViewerClientServices {

    private apiClient;
    public readonly viewerPagesRoutesManager;
    public readonly bymoTemplatesStore;
    public readonly actionTypesStore;
    public readonly viewerAnalyticsClient;
    public readonly uiService;
    public readonly bymoService;
    public readonly stripeService;
    public readonly lockService;
    public readonly doppeViewerClientData;
    public readonly doppeClientErrorsReportingService;
    public readonly doppeViewerAppCookieService;
    public readonly viewerBmAnalyticsDashboardBiClient;
    public readonly doppeUserAppData;
    public readonly viewerRouterClientService;

    constructor (
        services: {
            doppeUserAppData: DoppeUserAppData;
            apiClient: RestfulApiHttpClient<IDoppeViewerApi>;
            viewerPagesRoutesManager: DoppeViewerPagesRoutesManager;
            bymoTemplatesStore: BymoTemplatesStore;
            doppeActionTypesStore: DoppeActionTypesStore;
            doppeViewerAppAnalyticsClient: DoppeViewerAppAnalyticsClient;
            doppeViewerAppCookieService: DoppeViewerAppCookieService;
            uiService: DoppeViewerAppUiService;
            bymoService: DoppeViewerAppBymoService;
            stripeService: DoppeViewerAppStripeService;
            lockService: DoppeViewerAppLockService;
            doppeViewerClientData: DoppeViewerClientDataService;
            doppeClientErrorsReportingService: DoppeClientErrorsReportingService;
            viewerBmAnalyticsDashboardBiClient: DoppeViewerBmAnalyticsDashboardBiClient;
            viewerRouterClientService: DoppeViewerRouterClientService;
        }
    ) {
        this.apiClient = services.apiClient;
        this.viewerPagesRoutesManager = services.viewerPagesRoutesManager;
        this.bymoTemplatesStore = services.bymoTemplatesStore;
        this.actionTypesStore = services.doppeActionTypesStore;
        this.viewerAnalyticsClient = services.doppeViewerAppAnalyticsClient;
        this.doppeUserAppData = services.doppeUserAppData;
        this.uiService = services.uiService;
        this.bymoService = services.bymoService;
        this.stripeService = services.stripeService;
        this.lockService = services.lockService;
        this.doppeViewerClientData = services.doppeViewerClientData;
        this.doppeClientErrorsReportingService = services.doppeClientErrorsReportingService;
        this.doppeViewerAppCookieService = services.doppeViewerAppCookieService;
        this.viewerBmAnalyticsDashboardBiClient = services.viewerBmAnalyticsDashboardBiClient;
        this.viewerRouterClientService = services.viewerRouterClientService;
    }

    public getApiClient () {
        return this.apiClient;
    }
}

export const DoppeViewerClientServicesReactContext = React.createContext<DoppeViewerClientServices | null>(null);

export function useDoppeViewerClientServices () {
    return assertNotNullable(useContext(DoppeViewerClientServicesReactContext));
}

export function doppeViewerClientServicesInitialize (
    evaluableOverrides: Evaluable<(deferredDoppeViewerAppClientServices: InitializableValue<DoppeViewerClientServices>) => Partial<ConstructorParameters<typeof DoppeViewerClientServices>[0]>> = {}
) {

    const deferredDoppeViewerAppClientServices = new InitializableValue<DoppeViewerClientServices>();
    const options = evaluateWhenFunction(evaluableOverrides, deferredDoppeViewerAppClientServices);
    const doppeUserAppData = options.doppeUserAppData ?? new DoppeUserAppData();

    const asyncCachedSignedInstance = asyncCachedValueCreate(async () => {

        if (doppeUserAppData.viewerAccessTokensUrl && (window as any).__viewerClientSessionValue !== undefined) {
            const httpClient = await doppeClientHttpClientCreate();

            return wixDynamicModelV2ResolveSignedInstance(
                (await httpClient.get<WixDynamicModelV2>(doppeUserAppData.viewerAccessTokensUrl, {
                    headers: {
                        'client-binding': (window as any).__viewerClientSessionValue
                    }
                })).data,
                WixAppsIds.MetaSite
            );
        }

        if (doppeUserAppData.dynamicModelApiUrl) {
            const httpClient = await doppeClientHttpClientCreate();

            return wixDynamicModelV2ResolveSignedInstance(
                (await httpClient.get<WixDynamicModelV2>(doppeUserAppData.dynamicModelApiUrl)).data,
                WixAppsIds.MetaSite
            );
        }
        
        return undefined;
    })

    const viewerPagesRoutesManager = options.viewerPagesRoutesManager ?? 
        new DoppeViewerPagesRoutesManager({ 
            basePath: doppeUserAppData.appBasePath
        });

    const clientServices = new DoppeViewerClientServices({
        doppeUserAppData: doppeUserAppData,
        viewerPagesRoutesManager: viewerPagesRoutesManager,
        doppeViewerClientData: options.doppeViewerClientData ?? new DoppeViewerClientDataService(deferredDoppeViewerAppClientServices),
        apiClient: options.apiClient ?? restfulApiHttpClientCreate(
            async () => {

                const {DoppeViewerApiRoutesManager} = await import(/* webpackChunkName: "doppe-viewer-api-routes-manager" */ '../../client-server-common/api/doppe-viewer-api-routes-manager');

                return new DoppeViewerApiRoutesManager({
                    basePath: doppeUserAppData.viewerApiBasePath
                })
            },
            {
                httpClient: doppeClientHttpClientCreate,
                baseUrl: doppeUserAppData.apiBaseUrl,
                withCredentials: false,
                usePostInsteadOfBeacon: valueIsNotNullable(doppeUserAppData.deployPreview),
                commonHeaders: () => {
                    return {
                        ...wixDeployPreviewGetHeaders(doppeUserAppData.deployPreview),
                    }
                },
                processEndpointUrl: async url => {
                    const signedInstance = await asyncWithFallback(asyncCachedSignedInstance.getValue(), undefined);

                    if (signedInstance) {
                        return urlModifyQueryParams(url, queryParams => ({
                            ...queryParams,
                            'instance': signedInstance
                        }));
                    } else {
                        return url;
                    }
                }
            }),
        uiService: new DoppeViewerAppUiService(deferredDoppeViewerAppClientServices),
        bymoTemplatesStore: options.bymoTemplatesStore ?? new BymoTemplatesStore(),
        doppeActionTypesStore: options.doppeActionTypesStore ?? doppeActionTypesStoreCreate(),
        doppeViewerAppAnalyticsClient: options.doppeViewerAppAnalyticsClient ?? new DoppeViewerAppAnalyticsClient(deferredDoppeViewerAppClientServices),
        bymoService: options.bymoService ?? new DoppeViewerAppBymoService(deferredDoppeViewerAppClientServices),
        stripeService: options.stripeService ?? new DoppeViewerAppStripeService(deferredDoppeViewerAppClientServices),
        lockService: options.lockService ?? new DoppeViewerAppLockService(deferredDoppeViewerAppClientServices),
        doppeClientErrorsReportingService: options.doppeClientErrorsReportingService ?? new DoppeClientErrorsReportingService(),
        doppeViewerAppCookieService: options.doppeViewerAppCookieService ?? new DoppeViewerAppCookieService(deferredDoppeViewerAppClientServices),
        viewerBmAnalyticsDashboardBiClient: options.viewerBmAnalyticsDashboardBiClient ?? new DoppeViewerBmAnalyticsDashboardBiClient(deferredDoppeViewerAppClientServices),
        viewerRouterClientService: options.viewerRouterClientService ?? new DoppeViewerRouterClientService(deferredDoppeViewerAppClientServices)
    });

    deferredDoppeViewerAppClientServices.initialize(clientServices);

    if (!options.doppeClientErrorsReportingService) {
        clientServices.doppeClientErrorsReportingService.initialize({
            appVersion: doppeUserAppData.appVersion,
            environment: doppeUserAppData.appRuntimeEnvironment,
            appType: 'DoppeAppViewer',
            getInitialScope: () => ({
                user: {
                    id: clientServices.doppeViewerClientData?.clientId,
                },
            }),
        })
            .catch(error => {
                console.error(`Failed initializing doppeClientErrorsReportingService`, error);
            });
    }

    return clientServices;
}
